import { useCallback, useEffect, useState } from "react";
import { Search } from "react-bootstrap-table2-toolkit";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { Skeleton } from "@material-ui/lab";
import {
  Create,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from "@material-ui/icons";

import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import DialogMotivosFalhas from "components/Dialogs/DialogMotivosFalhas";
import ToggleCell from "components/Cells/ToggleCell";
import IconButtonLoader from "components/Buttons/IconButtonLoader";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import componentStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import Api from "lib/api/httpRequestHelper";
import { columnNumberField } from "utils/tables-utils";
import { columnTextField } from "utils/tables-utils";
import useLogs from "hooks/logs";
import { IsMobile, IsMobile500 } from "utils/is-mobile";

const getTableColumns = (
  setSelectedReason,
  toggleFormDialog,
  toggleAtivo,
  searching
) => [
  columnNumberField(
    {
      dataField: "IdMotivoFalha",
      text: "ID",
      sort: true,
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnTextField(
    {
      dataField: "DataRegistroFormat",
      text: "Data de Registro",
      sort: true,
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
      align: "center",
    },
    true
  ),
  columnTextField(
    {
      dataField: "DescMotivoFalha",
      text: "Descrição",
      sort: true,
      formatter: (row) => (searching ? <Skeleton variant="text" /> : row),
    },
    true
  ),
  columnNumberField(
    {
      dataField: "StatusMotivoFalha",
      text: "Status",
      sort: true,
      formatter: (cell, row) => {
        return (
          <ToggleCell
            initialValue={row.StatusMotivoFalha}
            handleChange={async (params) => {
              row.StatusMotivoFalha = params.StatusMotivoFalha;

              await toggleAtivo({ ...row, ...params });
            }}
            initialLoading={searching}
            field="StatusMotivoFalha"
          />
        );
      },
    },
    true
  ),
  {
    dataField: "editar",
    text: "",
    isDummyField: true,
    headerStyle: { width: 45 },
    formatter: (cell, row) => {
      return searching ? null : (
        <Tooltip title="Editar Motivo">
          <IconButton
            aria-label="edit"
            onClick={() => {
              setSelectedReason(row);
              toggleFormDialog("edit");
            }}
          >
            <Box
              component={Create}
              width="1.25rem!important"
              height="1.25rem!important"
            />
          </IconButton>
        </Tooltip>
      );
    },
  },
];

const loadingItems = [
  { IdMotivoFalha: "0" },
  { IdMotivoFalha: "1" },
  { IdMotivoFalha: "2" },
  { IdMotivoFalha: "3" },
  { IdMotivoFalha: "4" },
  { IdMotivoFalha: "5" },
  { IdMotivoFalha: "6" },
  { IdMotivoFalha: "7" },
  { IdMotivoFalha: "8" },
  { IdMotivoFalha: "9" },
];

const { SearchBar } = Search;

const useStyles = makeStyles(componentStyles);

function MotivosDeFalha() {
  const classes = useStyles();
  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem, setLocalStorageActions } = useLogs();

  const [reasons, setReasons] = useState(loadingItems);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [searching, setSearching] = useState(true);
  const [hasCreated, setHasCreated] = useState(false);

  const isMobile = IsMobile();

  const isMobile500 = IsMobile500();

  const toggleFormDialog = useCallback((formType) => {
    if (formType) {
      setOpenFormDialog(formType);
    }
  }, []);

  const toggleAtivo = (updatedReason) => {
    return Api.put(
      "edit-fail-motive",
      "weave",
      {
        descricao: updatedReason.DescMotivoFalha.toString(),
        status: updatedReason.StatusMotivoFalha.toString(),
        idMotivoFalha: updatedReason.IdMotivoFalha.toString(),
      },
      true
    )
      .then((res) => {
        if (res.Success) {
          snackbar.showMessage(
            `Motivo ${updatedReason.IdMotivoFalha.toString()} foi ${
              updatedReason.StatusMotivoFalha ? "ativado" : "desativado"
            } com sucesso!`,
            {
              severity: "success",
            }
          );
        } else {
          snackbar.showMessage(
            `Ocorreu um erro ao ${
              updatedReason.StatusMotivoFalha ? "ativar" : "desativar"
            } Motivo ${updatedReason.IdMotivoFalha.toString()}. Verifique os parâmetros informados.`,
            {
              severity: "error",
            }
          );
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "133",
          screen: "Motivos de Falha",
          problem: `Erro na chamada do endpoint edit-fail-motive - ${error}`,
        });
      });
  };

  const updateList = async (openFormDialog, updatedReason) => {
    const newMenuList = reasons?.map((reason) => {
      if (reason.IdMotivoFalha === updatedReason.IdMotivoFalha)
        return updatedReason;
      else return reason;
    });

    setReasons(newMenuList);
  };

  const fetchList = useCallback(async () => {
    setSearching(true);

    await Api.get("weave-fail-motive-list", "weave", "", true)
      .then((res) => {
        setSearching(false);
        setReasons(res.Result);
        setHasCreated(false);
      })
      .catch((error) => {
        setSearching(false);
        console.log(error);
        snackbar.showMessage(
          "Ocorreu um erro ao buscar motivos de falha. Verifique os parâmetros informados e/ou tente novamente mais tarde.",
          {
            severity: "error",
          }
        );
        setLocalStorageApiProblem({
          idMenuAccess: "133",
          screen: "Motivos de Falha",
          problem: `Erro na chamada do endpoint weave-fail-motive-list - ${error}`,
        });
      });
  }, [snackbar]);

  const doSearch = useCallback(() => {
    setSearching(true);

    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchList();
  }, [hasCreated]);

  useEffect(() => {
    fetchList();
    setLocalStorageActions({
      nomeTela: "Motivos de Falha",
      idMenu: "133",
      acao: "Acessou menu Motivos de Falha",
    });
  }, []);

  return (
    <>
      <HeaderClientes section="Motivos de Falha" subsection="Tecelagem" />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Grid item>
              <ToolkitTable
                keyField="IdMotivoFalha"
                dataTable={reasons}
                allowEmpty
                noResultTitle="Não há motivos de falhas cadastrados."
                noResultSubtitle="Clique em + Novo Motivo para começar!"
                columnModel={getTableColumns(
                  setSelectedReason,
                  toggleFormDialog,
                  toggleAtivo,
                  searching
                )}
                headerTitle={`MotivosFalha`}
                csvFileName="MotivosFalha.csv"
                showSearch={false}
                pagination={{
                  options: [25, 50, 100],
                  defaultOption: 25,
                }}
                CustomHeader={({ searchProps }) => (
                  <Grid
                    container
                    className="pl-4"
                    style={{ padding: "0 24px", marginBottom: "1rem" }}
                  >
                    <Grid item xs={isMobile ? 12 : 4}>
                      <Box
                        component={Typography}
                        variant="h2"
                        marginBottom="0!important"
                      >
                        <Box component="span">Lista de Motivos</Box>
                        <IconButtonLoader
                          size="medium"
                          extraStyle={{
                            width: 24,
                            height: 24,
                            marginLeft: "0.3rem",
                          }}
                          title="Recarregar lista"
                          onClick={doSearch}
                          icon={<RefreshIcon color="primary" />}
                          aria-label="refresh"
                          color="primary"
                          disabled={searching}
                        />
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs={isMobile ? 12 : 8}
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: isMobile ? "flex-start" : "flex-end",
                        flexDirection: isMobile500 ? "column" : "row",
                      }}
                    >
                      <SearchBar
                        className="form-control"
                        placeholder="Pesquisar Motivo"
                        {...searchProps}
                        disabled={searching}
                      />
                      <Box
                        justifyContent="flex-end"
                        display="flex"
                        flexWrap="wrap"
                        style={{ marginLeft: isMobile500 ? 0 : 15 }}
                      >
                        <Button
                          variant="contained"
                          classes={{
                            root: classes.buttonRootWhite,
                          }}
                          onClick={() => toggleFormDialog("create")}
                          disabled={searching}
                        >
                          <Box component={AddIcon} top="-2px" /> Novo Motivo
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
                showPrint
                loading={searching}
                notFoundTitle="Sua pesquisa não encontrou nenhum motivo de falha."
              />
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {Boolean(openFormDialog) && (
        <DialogMotivosFalhas
          open
          formType={openFormDialog}
          onClose={(reason) => {
            setSelectedReason(null);
            if (reason?.IdMotivoFalha > 0 || reason?.Id > 0) {
              if (openFormDialog === "create") {
                const newMenuList = [...reasons, { ...reason }];

                setReasons(newMenuList);
              } else {
                updateList(openFormDialog, reason);
              }
            }
            setOpenFormDialog(null);
          }}
          selectedReason={selectedReason}
          onCreated={(e) => setHasCreated(e)}
        />
      )}
    </>
  );
}

MotivosDeFalha.propTypes = {};

export default MotivosDeFalha;
